import React, { useRef, useEffect } from "react";
import { Box, Typography, Container } from "@mui/material";
import { trackScreenView } from "../lib/gtag";

export default function BBFNoThereThere() {
  const sectionRef = useRef();

  useEffect(() => {
    const node = sectionRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          trackScreenView("BBFNoThereThere");
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.3 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <Box
      ref={sectionRef}
      id="BBFNoThereThere"
      sx={{ py: 8, px: 2, textAlign: "left", backgroundColor: "#000", color: "#fff" }}
    >
      <Container maxWidth="md">
        <Typography variant="h4" gutterBottom fontWeight="bold">
          "There’s No There There?"
        </Typography>

      <Typography variant="body1" paragraph>
        That’s what former Secret Service agent <strong>Dan Bongino</strong> told <strong>Maria Bartiromo</strong> on <strong>May 19, 2025</strong>, downplaying the significance of motive or coordination in the Trump assassination attempt — asserting bluntly: <i>“in some of these cases, the there you’re looking for is not there.”</i>
      </Typography>

      <Typography variant="body1" paragraph>
        But <i>The Butler Brief</i> reveals what Bongino — and the entire federal apparatus — refused to admit: this case wasn’t just botched. It was buried.
      </Typography>

      <Typography variant="body1" paragraph>
        Senator Ron Johnson has subpoenaed FBI Director Kash Patel, demanding to know why the public, the press, and even the Trump family have been kept in the dark about <strong>motive, coordination, and potential accomplices</strong>.
      </Typography>

      <Typography variant="body1" paragraph>
        And yet — for all their funding and firepower, the feds came up empty.
      </Typography>

      <Typography variant="body1" paragraph>
        The FBI burns through <strong>$1 billion per month</strong>. The Secret Service spends another <strong>$300 million per month</strong>. That’s <strong>$1.3 billion</strong> every 30 days — and they still can’t explain how a 20-year-old with <strong>18 phones</strong> got within 150 yards of a former President.
      </Typography>

      <Typography variant="body1" paragraph>
        Meanwhile, we spent just <strong>$50/month</strong> — and uncovered everything they couldn't (or wouldn’t).
      </Typography>

        <ul>
          <li>
            Why was there <strong>no federal interest</strong> in the fact that <strong>Joe Biden told America to put Trump “in a bullseye”</strong> — just days before Trump was literally standing in one?
          </li>
          <li>
            Why hasn’t the FBI retrieved a single message, photo, or search from <strong>Thomas Crooks’ 18 phones</strong>? That’s right — <i>eighteen phones</i>. But somehow, <i>not one digital trail?</i>
          </li>
          <li>
            Why was Crooks <strong>cremated almost instantly</strong>, with <strong>no independent forensic review</strong> — and no questions from Congress?
          </li>
          <li>
            Why did the Secret Service <strong>deny repeated protection requests</strong> from Trump’s team — even as the FBI knew Trump was receiving elevated threats in the weeks before Butler?
          </li>
          <li>
            Why was <strong>Jill Biden speaking just 20 miles south</strong>, on the same crosshair line as Trump’s stage, <strong>minutes before the shooting</strong>? She had <i>the best security the feds could offer</i>. Trump, somehow, didn’t.
          </li>
          <li>
            Why did no one ask about the <strong>Rosemont Seneca Corridor</strong> — or why Biden’s bullseye rhetoric perfectly aligned with <strong>geospatial reality</strong>?
          </li>
          <li>
            Why hasn’t a single person in the FBI tried to understand the symbolism of Butler — the same place where <strong>George Washington nearly died</strong> — chosen as the final rally site before the RNC?
          </li>
        </ul>

        <Typography variant="body1" paragraph>
          At $1.3 billion a month, you'd think the federal government could afford a little <i>forensic cartography</i> — or maybe just Google Maps.
        </Typography>

        <Typography variant="body1" paragraph>
          This wasn’t just botched. It was buried.
        </Typography>

        <Typography variant="body1" paragraph>
          And yet — others looked.
        </Typography>

        <Typography variant="body1" paragraph>
          Trump’s team <strong>repeatedly requested additional protection</strong> for the Butler rally. According to a Senate report by <strong>Senator Rand Paul</strong>, the Secret Service denied those requests.
        </Typography>

        <Typography variant="body1" paragraph>
          On national television, <strong>Alexis Comperatore</strong>, sister of the slain firefighter, said it best:
        </Typography>

        <Typography variant="h6" sx={{ fontStyle: "italic", my: 2 }}>
          “There were layers upon layers of incompetence that day.”
        </Typography>

        <Typography variant="body1" paragraph>
          She was right. But those layers weren’t random. They were coordinated.
        </Typography>

        <Typography variant="body1" paragraph>
          At the exact moment Trump took the stage in Butler, <strong>Jill Biden</strong> was concluding a speech <strong>20 miles south</strong>, at the <strong>Rivers Casino in Pittsburgh</strong> — the <strong>southern anchor of the vertical crosshair</strong> in the <strong>Rosemont Seneca corridor</strong> of the <strong>Butler Bullseye</strong> narrative. Coupled with that location, the timing was not happenstance.
        </Typography>

        <Typography variant="body1" paragraph>
          She didn’t just speak at the perfect location. She spoke at the perfect time: <strong>one hour before the first shot was fired</strong>.
        </Typography>

        <Typography variant="body1" paragraph>
          For thousands of years, tyranny has cloaked itself in the <strong>veil of incompetence</strong>. As <strong>Cicero warned in 58 BC</strong>:
        </Typography>

        <Typography variant="h6" sx={{ fontStyle: "italic", my: 2 }}>
          “A nation can survive its fools… but not the traitor from within. <br />
          The traitor is the plague.”
        </Typography>

        <Typography variant="body1" paragraph>
          Even honest agents can't act inside a dishonest institution. The FBI and Secret Service failed to protect Trump in Florida. They failed again in Pennsylvania. And they looked the other way in Washington, D.C.
        </Typography>

        <Typography variant="body1" paragraph>
          And now, <strong>Congress has demanded answers</strong> — not just about the shooter, but about those who failed to stop him... or may have helped.
        </Typography>

        <Typography variant="body1" paragraph>
          Three strikes — and they’re out. The case was closed... until <i>The Butler Brief</i> reopened it.
        </Typography>
      </Container>
    </Box>
  );
}